import { useCallback, useMemo } from 'react';
import { Layout, Tabs, type TabsProps } from 'antd';
import { AppHeader } from 'pos-client/layouts/app/AppHeader.tsx';
import {
  PosPageContent,
  type PosPageContentProps,
} from 'pos-client/layouts/page/PosPageContent.tsx';
import { type PosPageHeaderProps } from 'pos-client/layouts/page/PosPageHeader.tsx';

import './TabsOutlet.css';

type AntdTabItemType = NonNullable<TabsProps['items']>[number];

export interface PosTabItem extends AntdTabItemType {
  contentProps?: Omit<PosPageContentProps, 'children'>;
}

/**
 * Props for {@link PosTabs}
 */
export interface PosTabsProps extends Omit<TabsProps, 'type' | 'items'> {
  /**
   * Page header props @see {@link PosPageHeaderProps}
   * @remarks `pageTabs` needs to be true
   */
  headerProps?: Omit<PosPageHeaderProps, 'tabBar' | 'loading'>;
  /**
   * Hide page toolbar @see {@link PosPageHeaderProps}
   * @remarks `pageTabs` needs to be true
   * @defaultValue false
   */
  hideToolbar?: boolean;
  /** Disable tabs from sticky behaviour @defaultValue false */
  disableStickyTabs?: boolean;
  /**
   * Toggle page toolbar sticky behaviour @see {@link PosPageHeaderProps}
   * @defaultValue false
   * @remarks `pageTabs` needs to be true
   */
  stickyToolbar?: boolean;
  /** Tab bar loading state */
  loading?: boolean;
  /**
   * Prevent tab content from rendering until loading has completed
   * @defaulValue false
   */
  renderContentAfterLoad?: boolean;
  /** Array of tab items @see {@link PosTabItem} */
  items: PosTabItem[];
  /**
   * Set to true if tabs are using routes or are the primary content of the page
   * @defaultValue false
   */
  pageTabs?: boolean;
}

const TabComponent = ({
  pageTabs,
  ...props
}: TabsProps & { pageTabs?: boolean }) =>
  pageTabs ? (
    <Layout style={{ width: '30%' }}>
      <Tabs {...props} />
    </Layout>
  ) : (
    <Tabs {...props} />
  );

/**
 * @component
 * @description Tabs to display pages or content
 *
 * @see {@link https://ant.design/components/tabs | AntD Tabs Component}
 * @param props {@link PosTabs}
 */
export const PosTabs = ({
  headerProps,
  disableStickyTabs = false,
  stickyToolbar = false,
  hideToolbar = false,
  renderTabBar,
  items,
  loading = false,
  renderContentAfterLoad = false,
  pageTabs = false,
  ...tabProps
}: PosTabsProps) => {
  const tabBarRender: TabsProps['renderTabBar'] = useCallback<
    NonNullable<TabsProps['renderTabBar']>
  >(
    (props, DefaultTabBar) => {
      if (renderTabBar) {
        return renderTabBar(props, DefaultTabBar);
      }

      if (disableStickyTabs || !pageTabs) {
        return <DefaultTabBar {...props} />;
      }

      return (
        <AppHeader
          stickyToolbar={stickyToolbar}
          loading={loading}
          hideToolbar={hideToolbar}
          header={{
            ...headerProps,
            tabBar: (
              <DefaultTabBar
                {...props}
                style={{
                  marginBottom: 0,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.25)',
                  width: '100%',
                }}
              />
            ),
          }}
        />
      );
    },
    [
      disableStickyTabs,
      headerProps,
      hideToolbar,
      loading,
      pageTabs,
      renderTabBar,
      stickyToolbar,
    ]
  );

  const tabItems = useMemo(
    () =>
      items.map(({ key, children, contentProps, ...tab }) => ({
        key,
        ...tab,
        ...(renderContentAfterLoad && loading
          ? {
              children: undefined,
            }
          : {
              children: contentProps ? (
                <PosPageContent key={key} {...contentProps}>
                  {children}
                </PosPageContent>
              ) : (
                children
              ),
            }),
      })),
    [items, loading, renderContentAfterLoad]
  );

  return (
    <TabComponent
      className="tabs-outlet page-tabs"
      renderTabBar={tabBarRender}
      items={tabItems}
      pageTabs={pageTabs}
      {...tabProps}
    />
  );
};
